import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Play, Pause, Trash2, Store, MapPin, Loader2 } from 'lucide-react';
import { StatusBadge, EmptyState, LoadingSpinner } from './shared';
import { toast } from 'sonner';

export const StoresSection = ({ stores, loading, onApprove, onTogglePause, onDeleteStore, onSaveBankgiro }) => {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bankgiroDrafts, setBankgiroDrafts] = useState({});
  const [savingBankgiroId, setSavingBankgiroId] = useState(null);

  useEffect(() => {
    const next = {};
    stores.forEach((s) => {
      next[s.id] = s.bankgiro ?? '';
    });
    setBankgiroDrafts(next);
  }, [stores]);

  const handleSaveBankgiro = async (store) => {
    const value = (bankgiroDrafts[store.id] ?? '').trim();
    if (value === (store.bankgiro ?? '').trim()) return;
    setSavingBankgiroId(store.id);
    try {
      await onSaveBankgiro(store.id, value);
    } catch {
      toast.error('Kunde inte spara bankgiro');
    } finally {
      setSavingBankgiroId(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4" data-testid="admin-stores">
      {stores.length > 0 ? stores.map(store => (
        <div key={store.id} className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={`store-row-${store.id}`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Store className="h-6 w-6 text-[#003B73]" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-bold text-[#0F172A]">{store.name}</h4>
                  <StatusBadge status={store.status} />
                  {store.is_paused && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold">PAUSAD</span>}
                </div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />{store.location || store.county || '-'}
                </p>
                <p className="text-xs text-gray-400">{store.contact_email}</p>
                {store.org_number ? (
                  <p className="text-xs text-gray-400 mt-0.5">Org.nr: {store.org_number}</p>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {store.status === 'pending' && (
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-lg" onClick={() => onApprove(store.id)} data-testid={`approve-store-${store.id}`}>
                  Godkänn
                </Button>
              )}
              <Button
                size="sm"
                variant={store.is_paused ? 'default' : 'outline'}
                className={`rounded-lg ${store.is_paused ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                onClick={() => onTogglePause(store.id)}
                title={store.is_paused ? 'Aktivera butik' : 'Pausa butik'}
                data-testid={`toggle-store-pause-${store.id}`}
              >
                {store.is_paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-lg border-red-200 text-red-500 hover:bg-red-50"
                onClick={() => setDeleteTarget(store)}
                title="Ta bort butik"
                data-testid={`delete-store-${store.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-end gap-2 max-w-lg">
            <div className="flex-1">
              <Label htmlFor={`bankgiro-${store.id}`} className="text-xs text-gray-500">
                Bankgiro (utbetalningar)
              </Label>
              <Input
                id={`bankgiro-${store.id}`}
                className="mt-1"
                value={bankgiroDrafts[store.id] ?? ''}
                onChange={(e) => setBankgiroDrafts({ ...bankgiroDrafts, [store.id]: e.target.value })}
                placeholder="123-4567"
                data-testid={`store-bankgiro-${store.id}`}
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-lg shrink-0"
              disabled={
                savingBankgiroId === store.id
                || (bankgiroDrafts[store.id] ?? '').trim() === (store.bankgiro ?? '').trim()
              }
              onClick={() => handleSaveBankgiro(store)}
              data-testid={`save-bankgiro-${store.id}`}
            >
              {savingBankgiroId === store.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                'Spara bankgiro'
              )}
            </Button>
          </div>
        </div>
      )) : (
        <EmptyState message="Inga butiker hittades" />
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Ta bort butik</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Är du säker på att du vill ta bort denna butik?
            </p>
            {deleteTarget && (
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="font-semibold text-gray-900">{deleteTarget.name}</p>
                <p className="text-sm text-gray-500">{deleteTarget.contact_email || deleteTarget.location || '-'}</p>
              </div>
            )}
            <p className="text-xs text-red-500">Detta raderar butiken och alla tillhörande produkter permanent.</p>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" className="rounded-xl">Avbryt</Button></DialogClose>
            <Button
              className="bg-red-600 hover:bg-red-700 rounded-xl"
              onClick={() => { onDeleteStore(deleteTarget.id); setDeleteTarget(null); }}
              data-testid="confirm-delete-store-btn"
            >
              Ta bort
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
